import { useEffect, useRef, useState } from "react";
import api from "@/utils/axios-utils";
import PracticeDiagramFrame from "@/components/practiceDiagram/PracticeDiagramFrame";
import { usePracticeDiagram } from "@/components/practiceDiagram/practiceDiagramLoader";
import { SLIDE_HEIGHT, SLIDE_WIDTH } from "@/constants/slideDeck";
import { useFitScale } from "@/hooks/useFitScale";

/**
 * The diagram behind a practice bite, rendered for the teacher as the bite's
 * visual identifier.
 *
 * The session screenshot is useless here: it captures `.reveal`, and the
 * notepad canvas mounts beside it, so the student's work is never in frame.
 * What identifies the bite is the problem diagram on the `practice_host` slide.
 *
 * Loaded through the shared practiceDiagramLoader cache and rendered by
 * PracticeDiagramFrame; this component only resolves the session's host url
 * and fits the frame to its box.
 *
 * Reports "missing" upward instead of rendering a placeholder — a practice
 * without a diagram is a supported authoring state.
 */
const PracticeHostPreview = ({
  sessionId,
  slideIdx,
  onUnavailable,
}: {
  sessionId: string;
  // String or number: callers reach here both from parsed bite ids and from
  // the numeric slide index — same widening as its sibling views.
  slideIdx: string | number;
  onUnavailable?: () => void;
}) => {
  // undefined while the host url is being resolved; null when there is none.
  const [htmlUrl, setHtmlUrl] = useState<string | null | undefined>(undefined);
  const [diagram] = usePracticeDiagram(htmlUrl, htmlUrl !== undefined);
  const stageRef = useRef<HTMLDivElement>(null);
  // Scale the fixed-size slide stage down to whatever box the parent gives us.
  const scale = useFitScale(
    stageRef,
    { width: SLIDE_WIDTH, height: SLIDE_HEIGHT },
    true,
    0,
    [diagram.status],
  );

  useEffect(() => {
    const controller = new AbortController();
    setHtmlUrl(undefined);
    api
      .get(
        `/analytics/sessions/${sessionId}/slides/${slideIdx}/practice-host`,
        {
          signal: controller.signal,
        },
      )
      .then((res) => setHtmlUrl(res.data?.html_url || null))
      .catch(() => {
        if (!controller.signal.aborted) setHtmlUrl(null);
      });
    return () => controller.abort();
  }, [sessionId, slideIdx]);

  // A 404, a failed load and an export with no `.slides section` all mean
  // the same here: nothing to show, so the panel collapses.
  const isUnavailable =
    diagram.status === "missing" || diagram.status === "error";
  useEffect(() => {
    if (isUnavailable) onUnavailable?.();
    // onUnavailable is intentionally not a dep — callers pass an inline arrow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnavailable]);

  if (isUnavailable) return null;

  return (
    <div
      ref={stageRef}
      className="relative w-full h-full overflow-hidden flex items-center justify-center bg-white"
    >
      {diagram.status === "loading" && (
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#E0E0E0] border-t-sagegreen" />
      )}
      {diagram.status === "ready" && scale > 0 && (
        <PracticeDiagramFrame
          diagram={diagram.diagram}
          scale={scale}
          title="Practice diagram"
          transformOrigin="center"
        />
      )}
    </div>
  );
};

export default PracticeHostPreview;
